import NoRecord from "@components/layouts/NoRecord";
import PageWrapper from "@components/layouts/PageWrapper";
import Search from "@components/layouts/Search";
import TableSkeleton from "@components/layouts/TableSkeleton";
import { newsColumnNames, newsColumns } from "@helpers/columns/cms/news.columns";
import { DeletePopup } from "@helpers/popups/DeletePopup";
import { Toast } from "@helpers/popups/Toast";
import useGetPermission from "@hooks/common/useGetPermission";
import { useDidMountEffect } from "@hooks/useDidMountEffect";
import { useErrorHandling } from "@hooks/useErrorHandling";
import usePagination from "@hooks/usePagination";
import useSearch from "@hooks/useSearch";
import { useDeleteNewsMutation, useGetAllNewsQuery } from "@states/cms/news.api";
import { TDeleteSuccesResponse } from "@type-defs/general/TDeleteSuccessResponse";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { NEWS } from "@utils/constants/cms/news.constants";
import { castTypeArr } from "@utils/general/cast-type";
import React, { useEffect } from "react";
import DataTable from "react-data-table-component";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

function NewsPage() {
  const { hasAccess } = useGetPermission();
  const canView =
    hasAccess("view", "CMS", "News") || hasAccess("view", "Others", "News");
  const canCreate =
    hasAccess("create", "CMS", "News") || hasAccess("create", "Others", "News");
  const canEdit =
    hasAccess("edit", "CMS", "News") || hasAccess("edit", "Others", "News");
  const canDelete =
    hasAccess("delete", "CMS", "News") || hasAccess("delete", "Others", "News");
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const { search, setSearch } = useSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    data,
    isFetching,
    error: getAllError,
    refetch,
  } = useGetAllNewsQuery({
    page,
    limit,
    search,
  });
  const [deleteNews, { error: deleteError }] = useDeleteNewsMutation();

  useErrorHandling(...castTypeArr<TFailResponse>([getAllError, deleteError]));

  useDidMountEffect(() => {
    refetch();
  }, [page, limit, search]);

  useEffect(() => {
    if (location.state?.reload) {
      refetch();
    }
  }, [location.state?.reload, refetch]);

  const editHandler = (id: number | string) => {
    navigate(NEWS.EDIT_PATH(id));
  };

  const deleteHandler = async (id: number | string) => {
    try {
      const { isConfirmed } = await DeletePopup.fire();

      if (isConfirmed) {
        const res = await deleteNews(id);

        const successData = "data" in res ? res.data : null;
        const { success, message } = successData as TDeleteSuccesResponse;

        if (success) {
          Toast.fire({
            icon: "success",
            title: message,
          });

          refetch();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  if (!canView) {
    return <Navigate to="/access-denied" />;
  }

  return (
    <React.Fragment>
      <Search setSearch={setSearch} />
      <PageWrapper
        addPath={NEWS.ADD_PATH()}
        addButtonTitle={NEWS.ADD_BUTTON_TITLE}
        title={NEWS.LIST_PAGE_TITLE}
        hasCreatePermission={canCreate}
      >
        <DataTable
          keyField="news_id"
          responsive
          striped
          pagination
          paginationServer
          paginationTotalRows={
            //@ts-ignore
            data?.paginator?.totalItems
          }
          progressPending={isFetching}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent={<NoRecord />}
          progressComponent={
            <TableSkeleton
              columnNames={newsColumnNames}
              actionRight
              row={10}
              col={5}
            />
          }
          //@ts-ignore
          columns={newsColumns(editHandler, deleteHandler, canEdit, canDelete)}
          //@ts-ignore
          data={data?.data}
        />
      </PageWrapper>
    </React.Fragment>
  );
}

export default NewsPage;
